// Match certainty (the owner's rule of 2026-10-20, D418): every proposed entity match carries a certainty in 0–1
// with the reasons behind it, and only a match at or above the matching threshold (Admin → Matching, default
// 0.90) is applied without a person. Everything below goes to human review ("Same entity?" in the Review Inbox).
// Used by the background pair finder, dataset imports and the skip commit of a document's review.
//
// The model (proven on the staging data pass of 2026-10-20):
//  - a base from the name evidence: same folded name 0.80; same once brackets, dash tails and generic words are
//    removed 0.78; a person's name words reordered 0.80; Jaro–Winkler ≥ 0.95 → 0.55 + (jw − 0.9) × 3, 0.90–0.95
//    → 0.50; partial evidence (an alias of the other 0.62, an acronym 0.60, a surname alone 0.55) and the same
//    name in another script (0.70, a similar one 0.55);
//  - type: +0.06 the same type, a parent/child type (Organization ↔ NGO) no change, −0.25 conflicting types;
//  - the name itself: +0.06 a distinctive multi-word name, +0.04 an all-caps acronym of 4+ letters, −0.10 a
//    one-word name, −0.30 a vague name ("the minister", a lower-case description), −0.30 one-word person names
//    on both sides;
//  - context: up to +0.10 shared linked entities, +0.06 shared documents, +0.10 a matching date of birth,
//    website, political group or event date; −0.30 a conflicting date of birth, website or event date; −0.15 an
//    undated event;
//  - caps: names that differ by a place, a year or an ordinal ("ELNET France" / "ELNET Germany", "8th" / "9th
//    Strategic Dialogue") never match strongly (≤ 0.40); partial and cross-script evidence stays ≤ 0.85 and never
//    applies on its own, whatever the threshold.
// Pure functions only: the claims service and the pipeline load the sides.

import { normalizeName } from './names';
import {
  type EquivalentIndex,
  isCrossScript,
  isOtherScript,
  jaroWinkler,
  type MatchSuggestion,
  nameSimilarity,
  type ResolutionQuery,
} from './resolution';

/** Matches at or above this certainty are applied without a person (unless the admin sets another). */
export const DEFAULT_MATCH_THRESHOLD = 0.9;
/** The range an admin may set the threshold in. */
export const MATCH_THRESHOLD_MIN = 0.5;
export const MATCH_THRESHOLD_MAX = 1;
/** Below this certainty a match is not proposed at all (no review item). */
export const MATCH_REVIEW_FLOOR = 0.5;
/** Partial and cross-script name evidence never scores above this. */
export const PARTIAL_MATCH_CAP = 0.85;
/** Names that differ by a place, a year or an ordinal never score above this. */
export const QUALIFIER_MATCH_CAP = 0.4;
/** A second candidate this close to the best one makes the choice a person's. */
export const MATCH_TIE_MARGIN = 0.05;
/** CONFIG (KV) key of the matching settings. */
export const MATCH_SETTINGS_KEY = 'settings:matching';

export interface MatchSettings {
  threshold: number;
}
export const DEFAULT_MATCH_SETTINGS: MatchSettings = { threshold: DEFAULT_MATCH_THRESHOLD };

/** Stored or submitted settings, clamped to the allowed range (anything unreadable is the default). */
export function parseMatchSettings(v: unknown): MatchSettings {
  const t = (v as { threshold?: unknown } | null)?.threshold;
  const n = typeof t === 'number' ? t : typeof t === 'string' ? Number(t) : Number.NaN;
  if (!Number.isFinite(n)) return { ...DEFAULT_MATCH_SETTINGS };
  return { threshold: Math.round(Math.min(MATCH_THRESHOLD_MAX, Math.max(MATCH_THRESHOLD_MIN, n)) * 100) / 100 };
}

/** The matching settings in CONFIG (KV); the default when none are stored or KV can't be read. */
export async function loadMatchSettings(
  kv: { get(key: string, type: 'json'): Promise<unknown> } | null | undefined,
): Promise<MatchSettings> {
  try {
    return parseMatchSettings(kv ? await kv.get(MATCH_SETTINGS_KEY, 'json') : null);
  } catch {
    return { ...DEFAULT_MATCH_SETTINGS };
  }
}

/** Attributes the model compares (normalized by the loader to plain strings). */
export interface MatchAttributes {
  dateOfBirth?: string | null;
  website?: string | null;
  politicalGroup?: string | null;
  /** An event's date. */
  date?: string | null;
}

/** One side of a proposed match: an existing entity, a dataset row, or an extracted entity. */
export interface MatchSide {
  name: string;
  /** Other names (aliases, other scripts). */
  aliases?: readonly string[];
  kind?: string | null;
  /** The type key and its ancestors, own key first (`['ngo', 'organization', 'actor']`); empty when unknown. */
  typeChain?: readonly string[];
  /** Ids of the entities it is linked to. */
  linkedIds?: readonly string[];
  /** Ids of the documents behind it. */
  documentIds?: readonly string[];
  attributes?: MatchAttributes;
}

export type MatchReasonCode =
  | 'same_name'
  | 'same_core_name'
  | 'reordered_name'
  | 'similar_spelling'
  | 'alias'
  | 'acronym'
  | 'surname_only'
  | 'other_script'
  | 'no_name_match'
  | 'same_type'
  | 'compatible_type'
  | 'conflicting_type'
  | 'distinctive_name'
  | 'acronym_name'
  | 'one_word_name'
  | 'vague_name'
  | 'one_word_people'
  | 'shared_links'
  | 'shared_documents'
  | 'matching_attributes'
  | 'conflicting_attributes'
  | 'undated_event'
  | 'qualifier_differs'
  | 'partial_cap';

export interface MatchReason {
  code: MatchReasonCode;
  /** What a reviewer reads ("Same name", "Different date of birth"). */
  label: string;
  /** What it added to (or took from) the certainty; the base for name evidence; 0 for a cap note. */
  delta: number;
}

/** A stored reason (pair evidence is JSON). */
export const isMatchReason = (v: unknown): v is MatchReason =>
  !!v &&
  typeof v === 'object' &&
  typeof (v as MatchReason).code === 'string' &&
  typeof (v as MatchReason).label === 'string' &&
  typeof (v as MatchReason).delta === 'number';

export interface MatchCertainty {
  /** 0–1, two decimals. */
  score: number;
  reasons: MatchReason[];
  /** Partial name evidence (alias, acronym, surname alone): never applied on its own. */
  partial: boolean;
  /** The names are in different scripts or languages: never applied on its own (PLAN §6.1). */
  crossScript: boolean;
}

/** Words dropped from an organization's core name ("The …", legal forms). */
const GENERIC = new Set([
  'the',
  'a',
  'an',
  'le',
  'la',
  'les',
  'l',
  'der',
  'die',
  'das',
  'inc',
  'ltd',
  'llc',
  'plc',
  'co',
  'corp',
  'sa',
  'sas',
  'sarl',
  'srl',
  'gmbh',
  'ev',
  'asbl',
  'aisbl',
  'vzw',
]);
/** Particles that don't make a name distinctive. */
const PARTICLES = new Set([
  'of',
  'for',
  'and',
  'et',
  'und',
  'de',
  'des',
  'du',
  'van',
  'von',
  'al',
  'el',
  'ibn',
  'bin',
  'ben',
  'on',
  'in',
  'to',
]);
/** Words skipped when building an acronym. */
const ACRONYM_SKIP = new Set([...PARTICLES, ...GENERIC]);
const ORDINAL_WORDS = new Set([
  'first',
  'second',
  'third',
  'fourth',
  'fifth',
  'sixth',
  'seventh',
  'eighth',
  'ninth',
  'tenth',
  'premier',
  'premiere',
  'deuxieme',
  'troisieme',
  'erste',
  'zweite',
  'dritte',
]);
const ROMAN = /^(?=[ivxlc]+$)c{0,3}(xc|xl|l?x{0,3})(ix|iv|v?i{0,3})$/;
/** Countries, demonyms and cities that tell chapters, branches and editions apart (folded). */
const PLACES = new Set(
  [
    'france french francaise francais paris lyon marseille',
    'germany deutschland allemagne german deutsche deutsch berlin munich muenchen frankfurt',
    'belgium belgique belgie belgian brussels bruxelles brussel',
    'netherlands nederland dutch holland amsterdam',
    'italy italia italie italian rome roma milan',
    'spain espana espagne spanish madrid barcelona',
    'portugal portuguese lisbon lisboa',
    'austria osterreich oesterreich autriche vienna wien',
    'switzerland suisse schweiz swiss geneva geneve zurich',
    'poland polska polish warsaw',
    'sweden swedish stockholm norway norwegian oslo denmark danish copenhagen finland finnish helsinki',
    'ireland irish dublin uk britain british england english london scotland wales',
    'greece greek athens cyprus hungary budapest czech prague romania bucharest bulgaria sofia',
    'europe european eu usa us america american washington canada canadian ottawa',
    'israel israeli jerusalem tel aviv palestine palestinian gaza ramallah',
    'lebanon lebanese beirut syria damascus jordan amman egypt cairo iran tehran iraq baghdad',
    'turkey turkiye ankara istanbul qatar doha uae emirates dubai saudi riyadh morocco rabat',
    'russia russian moscow ukraine ukrainian kyiv kiev china chinese beijing',
    'north south east west northern southern eastern western',
  ]
    .join(' ')
    .split(' '),
);

const words = (s: string) => normalizeName(s).split(' ').filter(Boolean);

/** A name without brackets, dash tails and generic words ("ELNET – European Leadership Network" → "elnet"). */
export function coreName(value: string): string {
  const stripped = value
    .replace(/\([^)]*\)|\[[^\]]*\]/g, ' ')
    .replace(/\s[-–—]\s.*$/, ' ')
    .trim();
  const ws = words(stripped || value);
  const kept = ws.filter(w => !GENERIC.has(w));
  return (kept.length ? kept : ws).join(' ');
}

const isQualifier = (w: string) =>
  /^(1[89]\d\d|20\d\d)$/.test(w) || /^\d+(st|nd|rd|th|e|er|eme|re|te)?$/.test(w) || ORDINAL_WORDS.has(w) || ROMAN.test(w) || PLACES.has(w);

/** The words only one of the names has, when one of them is a place, a year or an ordinal. */
export function differingQualifiers(a: string, b: string): string[] {
  const wa = new Set(words(a));
  const wb = new Set(words(b));
  const only = [...[...wa].filter(w => !wb.has(w)), ...[...wb].filter(w => !wa.has(w))];
  return only.filter(isQualifier);
}

const isAcronym = (value: string) => /^[A-Z0-9&]{4,}$/.test(value.trim()) && /[A-Z]{3,}/.test(value);
const acronymOf = (value: string) =>
  words(value)
    .filter(w => !ACRONYM_SKIP.has(w))
    .map(w => w[0])
    .join('');
/** "the minister", "a senior official", "unnamed businessman": a description, not a name. */
const isVague = (value: string) => {
  const v = value.trim();
  if (/^(the|a|an|some)\s+\p{Ll}/iu.test(v) && /^\S+\s+\p{Ll}/u.test(v)) return true;
  if (/^(unnamed|unknown|unidentified)\s/i.test(v)) return true;
  return /\p{Ll}/u.test(v) && !/\p{Lu}/u.test(v) && /^\p{Script=Latin}/u.test(v) && v.includes(' ');
};

const isPerson = (s: MatchSide) => !!s.typeChain?.includes('person');
const isOrg = (s: MatchSide) => !!s.typeChain?.includes('organization');

/** Folded website host and path ("https://www.elnet.org/" → "elnet.org"). */
const site = (v: string) =>
  v
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, '')
    .replace(/^www\./, '')
    .replace(/[/?#]+$/, '');
/** A date as its digits (2021-03-04 → "20210304"); a year alone stays four digits. */
const day = (v: string) => (v.match(/\d+/g) ?? []).join('').slice(0, 8);
/** Two dates agree when one is a prefix of the other (a year and a full date of that year agree). */
const datesAgree = (a: string, b: string) => {
  const x = day(a);
  const y = day(b);
  if (!x || !y) return null;
  return x.startsWith(y) || y.startsWith(x);
};

interface NameEvidence {
  base: number;
  code: MatchReasonCode;
  label: string;
  partial: boolean;
  crossScript: boolean;
}

/** The strongest name evidence between the two sides (primary names first, then aliases). */
function nameEvidence(a: MatchSide, b: MatchSide, equivalents?: EquivalentIndex): NameEvidence | null {
  const na = normalizeName(a.name);
  const nb = normalizeName(b.name);
  const out: NameEvidence[] = [];
  const add = (base: number, code: MatchReasonCode, label: string, partial = false, crossScript = false) =>
    out.push({ base, code, label, partial, crossScript });
  if (na && na === nb) add(0.8, 'same_name', 'Same name');
  const ca = coreName(a.name);
  const cb = coreName(b.name);
  if (ca && ca === cb && na !== nb) add(0.78, 'same_core_name', 'Same name once brackets, dash tails and generic words are removed');
  const wa = words(a.name);
  const wb = words(b.name);
  if (
    !isOrg(a) &&
    !isOrg(b) &&
    wa.length >= 2 &&
    wa.length === wb.length &&
    na !== nb &&
    [...wa].sort().join(' ') === [...wb].sort().join(' ')
  )
    add(0.8, 'reordered_name', 'Same name words in another order');
  if (na && nb && na !== nb) {
    const jw = jaroWinkler(na, nb);
    if (jw >= 0.95) add(0.55 + (jw - 0.9) * 3, 'similar_spelling', `Very similar spelling (${Math.round(jw * 100)}% alike)`);
    else if (jw >= 0.9) add(0.5, 'similar_spelling', `Similar spelling (${Math.round(jw * 100)}% alike)`);
  }
  // Partial evidence: an alias of the other, an acronym, a surname alone.
  const allA = [a.name, ...(a.aliases ?? [])].map(normalizeName).filter(Boolean);
  const allB = [b.name, ...(b.aliases ?? [])].map(normalizeName).filter(Boolean);
  if (na !== nb && allA.some(x => allB.includes(x))) add(0.62, 'alias', 'One is known by a name of the other (alias)', true);
  for (const [short, long] of [
    [a.name, b.name],
    [b.name, a.name],
  ] as const) {
    const s = words(short);
    const l = words(long);
    if (s.length === 1 && l.length >= 2 && s[0] && s[0].length >= 2 && acronymOf(long) === s[0])
      add(0.6, 'acronym', 'One name is the acronym of the other', true);
    if (s.length === 1 && l.length >= 2 && s[0] && s[0].length >= 3 && l.at(-1) === s[0] && !isOrg(a) && !isOrg(b))
      add(0.55, 'surname_only', 'Only the surname matches', true);
  }
  // The same name in another script or language (Hasan Haddad / حسن حداد).
  for (const x of [a.name, ...(a.aliases ?? [])])
    for (const y of [b.name, ...(b.aliases ?? [])]) {
      if (!isCrossScript({ value: x }, { value: y })) continue;
      const m = nameSimilarity(x, y, equivalents);
      if (m.partial) continue;
      if (m.score >= 0.9) add(0.7, 'other_script', 'The same name written in another script', false, true);
      else if (m.score >= 0.7) add(0.55, 'other_script', 'A similar name written in another script', false, true);
    }
  if (!out.length) return null;
  // Strongest first; full evidence wins over partial evidence of the same strength.
  out.sort((x, y) => y.base - x.base || Number(x.partial) - Number(y.partial));
  return out[0] ?? null;
}

function typeReason(a: MatchSide, b: MatchSide): MatchReason | null {
  if (a.kind && b.kind && a.kind !== b.kind) return { code: 'conflicting_type', label: 'Different kinds of entity', delta: -0.25 };
  const ta = a.typeChain ?? [];
  const tb = b.typeChain ?? [];
  if (!ta.length || !tb.length) return null;
  if (ta[0] === tb[0]) return { code: 'same_type', label: 'Same type', delta: 0.06 };
  if (ta.includes(tb[0] ?? '') || tb.includes(ta[0] ?? ''))
    return { code: 'compatible_type', label: 'Compatible types (one is a kind of the other)', delta: 0 };
  return { code: 'conflicting_type', label: 'Conflicting types', delta: -0.25 };
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** How certain it is that two sides are the same entity, and why. */
export function matchCertainty(a: MatchSide, b: MatchSide, opts: { equivalents?: EquivalentIndex } = {}): MatchCertainty {
  const ev = nameEvidence(a, b, opts.equivalents);
  if (!ev)
    return {
      score: 0,
      reasons: [{ code: 'no_name_match', label: 'The names do not match', delta: 0 }],
      partial: false,
      crossScript: false,
    };
  const reasons: MatchReason[] = [{ code: ev.code, label: ev.label, delta: round2(ev.base) }];
  const add = (code: MatchReasonCode, label: string, delta: number) => reasons.push({ code, label, delta });

  const t = typeReason(a, b);
  if (t) reasons.push(t);

  // The name itself: distinctive, an acronym, one word, vague.
  const coreA = coreName(a.name).split(' ').filter(Boolean);
  const coreB = coreName(b.name).split(' ').filter(Boolean);
  const meaningful = (ws: string[]) => ws.filter(w => !PARTICLES.has(w) && w.length >= 2).length;
  if (isVague(a.name) || isVague(b.name)) add('vague_name', 'A vague name (a description, not a name)', -0.3);
  else if (isAcronym(a.name) && isAcronym(b.name)) add('acronym_name', 'An all-caps acronym of 4 or more letters', 0.04);
  else if (Math.min(meaningful(coreA), meaningful(coreB)) >= 2) add('distinctive_name', 'A distinctive name of several words', 0.06);
  else if (coreA.length <= 1 || coreB.length <= 1) {
    add('one_word_name', 'A one-word name', -0.1);
    if (isPerson(a) && isPerson(b) && coreA.length <= 1 && coreB.length <= 1)
      add('one_word_people', 'Both are people known by one word only', -0.3);
  }

  // Context.
  const linksA = new Set(a.linkedIds ?? []);
  const sharedLinks = [...new Set(b.linkedIds ?? [])].filter(id => linksA.has(id)).length;
  if (sharedLinks)
    add('shared_links', `${sharedLinks} linked ${sharedLinks === 1 ? 'entity' : 'entities'} in common`, Math.min(sharedLinks * 0.05, 0.1));
  const docsA = new Set(a.documentIds ?? []);
  const sharedDocs = [...new Set(b.documentIds ?? [])].filter(id => docsA.has(id)).length;
  if (sharedDocs) add('shared_documents', `Named in ${sharedDocs} of the same ${sharedDocs === 1 ? 'document' : 'documents'}`, 0.06);

  const xa = a.attributes ?? {};
  const xb = b.attributes ?? {};
  const agree: string[] = [];
  const conflict: string[] = [];
  if (xa.dateOfBirth && xb.dateOfBirth) {
    const same = datesAgree(xa.dateOfBirth, xb.dateOfBirth);
    if (same === true) agree.push('date of birth');
    else if (same === false) conflict.push('date of birth');
  }
  if (xa.website && xb.website) (site(xa.website) === site(xb.website) ? agree : conflict).push('website');
  if (xa.politicalGroup && xb.politicalGroup && normalizeName(xa.politicalGroup) === normalizeName(xb.politicalGroup))
    agree.push('political group');
  const isEvent = a.kind === 'event' || b.kind === 'event';
  if (isEvent) {
    if (xa.date && xb.date) {
      const same = datesAgree(xa.date, xb.date);
      if (same === true) agree.push('date');
      else if (same === false) conflict.push('date');
    } else add('undated_event', 'An event without a date', -0.15);
  }
  if (agree.length) add('matching_attributes', `Same ${agree.join(', ')}`, 0.1);
  if (conflict.length) add('conflicting_attributes', `Different ${conflict.join(', ')}`, -0.3);

  let score = reasons.reduce((s, r) => s + r.delta, 0);
  const qualifiers = differingQualifiers(a.name, b.name);
  if (qualifiers.length && score > QUALIFIER_MATCH_CAP) {
    add('qualifier_differs', `The names differ by a place, year or number (${qualifiers.slice(0, 3).join(', ')})`, 0);
    score = QUALIFIER_MATCH_CAP;
  }
  if ((ev.partial || ev.crossScript) && score > PARTIAL_MATCH_CAP) {
    add(
      'partial_cap',
      ev.crossScript ? 'Names in two scripts are always confirmed by a person' : 'Partial name evidence is always confirmed by a person',
      0,
    );
    score = PARTIAL_MATCH_CAP;
  }
  return {
    score: round2(Math.min(1, Math.max(0, score))),
    reasons: reasons.map(r => ({ ...r, delta: round2(r.delta) })),
    partial: ev.partial,
    crossScript: ev.crossScript,
  };
}

/** Whether a match may be applied without a person: at or above the threshold, on full same-script name evidence. */
export const appliesAutomatically = (c: Pick<MatchCertainty, 'score' | 'partial' | 'crossScript'>, threshold: number) =>
  c.score >= threshold && !c.partial && !c.crossScript;

/**
 * The one candidate a match may be applied to without a person: the best one, if it applies automatically and no
 * other candidate is within MATCH_TIE_MARGIN of it or also at the threshold (two strong candidates are a choice).
 */
export function pickAutomaticMatch<T extends { certainty: MatchCertainty }>(scored: readonly T[], threshold: number): T | null {
  const ranked = [...scored].sort((x, y) => y.certainty.score - x.certainty.score);
  const [best, next] = ranked;
  if (!best || !appliesAutomatically(best.certainty, threshold)) return null;
  if (next && (next.certainty.score >= threshold || next.certainty.score >= best.certainty.score - MATCH_TIE_MARGIN)) return null;
  return best;
}

/**
 * Whether a document's match suggestion becomes a "Same person?" question (D450): the names are in scripts they
 * don't share and the match certainty reaches the review floor. Two Latin names in different languages stay a
 * suggestion on the extracted entity; below the floor nothing is asked.
 */
export const queuesSamePersonPair = (
  s: Pick<MatchSuggestion, 'queryName' | 'matchedName'>,
  c: Pick<MatchCertainty, 'score'> | null | undefined,
): boolean => !!c && c.score >= MATCH_REVIEW_FLOOR && isOtherScript(s.queryName, s.matchedName);

/** "86%" for a certainty. */
export const certaintyPercent = (score: number) => `${Math.round(score * 100)}%`;

/**
 * The certainty of a resolution suggestion for an extracted entity (review step 1), from what the suggestion
 * knows: the names that matched, the type relation and the linked names both sides share.
 */
export function suggestionCertainty(
  q: Pick<ResolutionQuery, 'names' | 'kind'>,
  s: Pick<MatchSuggestion, 'queryName' | 'matchedName' | 'typeKey'> & {
    evidence: Pick<MatchSuggestion['evidence'], 'type' | 'sharedLinks'>;
  },
  opts: { equivalents?: EquivalentIndex } = {},
): MatchCertainty {
  const t = s.evidence.type;
  const queryChain = t === 'same' ? [s.typeKey] : t === 'conflict' ? ['other'] : [];
  const shared = s.evidence.sharedLinks.map(normalizeName);
  return matchCertainty(
    { name: s.queryName, aliases: q.names, kind: q.kind, typeChain: queryChain, linkedIds: shared },
    { name: s.matchedName, kind: q.kind, typeChain: t === 'same' || t === 'conflict' ? [s.typeKey] : [], linkedIds: shared },
    opts,
  );
}
